import type { ConfigurationEntry } from "@tdelivr/api-client/generated/tenant/tenant-configuration.js";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the two configuration boards do to the entries TEN-02 returns, away from any rendering.
 *
 * **Two pages, one listing, and that is the measurement rather than an oversight.**
 * `tests/page-support.test.ts` asks of every page of this console whether an operation answers a
 * list of the things the page is a list of, and `policies` and `config` both answer with
 * `TEN-02 GET /v1/configuration`. They are both boards of this tenant's configuration entries. What
 * separates them in the design is a third column — `Effective` on one, `Release` on the other — and
 * neither can be filled.
 *
 * **Nothing published separates a policy from an operational setting.** The listing's one filter is
 * `namespace`, which is a string behind `^[a-z][a-z0-9._-]{0,62}$` with no enumeration anywhere, so
 * there is no vocabulary to say "these namespaces are policies and those are operations". Both
 * boards therefore show every entry, and each says so under its grid rather than appearing to be a
 * subset. A page that silently drew the same rows under a different title would teach an
 * administrator that one of the two was broken.
 *
 * **`value` is any JSON value, including null, and the contract says why.** TEN-02's own
 * description: "The service records what a caller set and constrains nothing about it, so a
 * narrower schema here would describe a rule it does not enforce." So the `Value` column renders
 * whatever came back without interpreting it, and a `null` is the design's own
 * `caution · not yet set` row rather than a blank — that row, `Company registration · — · —`, is
 * where the design's `—` for an absent value comes from.
 */

/** The design's own two state words for a configuration entry, from its grid and its first tile. */
export type EntryState = "set" | "not yet set";

export const ENTRY_STATES: readonly EntryState[] = ["set", "not yet set"];

/**
 * Whether this entry holds a value.
 *
 * The one state TEN-02 publishes anything about. There is no status field on an entry: a tenant
 * either set the key or it is sitting at `null`, which the contract admits as a value a caller can
 * write. The design draws exactly that distinction — `done · set` on its first tile and
 * `caution · not yet set` on both its second tile and its `Company registration` row — so the two
 * words are the design's and the rule is the contract's.
 */
export function entryState(entry: ConfigurationEntry): EntryState {
  return entry.value === null || entry.value === undefined ? "not yet set" : "set";
}

/**
 * The design's `Value` cell, from a value whose type the contract deliberately does not constrain.
 *
 * A string is shown as stored, because that is what the design's values are —
 * `3 min after driver assigned`, `06:00–23:00 EAT daily`, `15`. Everything else is shown in its
 * JSON spelling, which is the only rendering that is not an interpretation: a boolean as `true`, a
 * number as itself, an object or an array compactly. `JSON.stringify` can return `undefined` for a
 * value it cannot represent, which `unknown` admits even though this contract's union does not, so
 * that case falls back to the design's `—` rather than printing the word "undefined".
 */
export function valueText(entry: ConfigurationEntry): string {
  const value = entry.value;
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  const written = JSON.stringify(value);
  return written ?? "";
}

/**
 * One row of either board.
 *
 * The `Setting` cell is `key` and the namespace is in the detail panel, which is a decision the
 * board's shape forces: the listing spans every namespace and pages over `(namespace, key)`, so two
 * namespaces holding the same key produce two rows whose first cell is identical. The design's own
 * configuration page writes dotted keys — `dispatch.offer.timeoutSeconds` — which is the grammar
 * TEN-02's pattern produces, and its policies page writes human names, which no field holds. R-40.
 *
 * @param third the page's own third column. `policies` passes `updatedAt` under its own heading
 *   (Q-101); `config` passes the absence (Q-104).
 */
export function entryRow(
  entry: ConfigurationEntry,
  third: { readonly text: string; readonly absent?: boolean; readonly title?: string },
): BoardRow {
  const state = entryState(entry);
  const unset = state === "not yet set";
  return {
    // `(namespace, key)` is the entry's identity and the listing's own cursor key. There is no `id`.
    id: `${entry.namespace}/${entry.key}`,
    cells: [
      { text: entry.key, mono: true },
      {
        text: valueText(entry),
        ...(unset
          ? {
              absent: true,
              title:
                "TEN-02 holds a null for this key. The contract admits null as a value a caller can write, so this is a setting nobody has set rather than a field the service is missing.",
            }
          : {}),
      },
      third,
    ],
    state: unset
      ? {
          classKey: "caution",
          label: "not yet set",
          title:
            "The entry exists and its value is null. Anything reading this setting gets a null, including the published policies page.",
        }
      : { classKey: "done", label: "set" },
    // The design tints only its `human` and `failed` rows, and no configuration entry reaches
    // either: there are two states and they are `done` and `caution`.

    /*
     * One line where the design's panel lists three, and it is here because the board cannot be
     * read without it: this listing spans every namespace, the `Setting` column is the key alone,
     * and two namespaces can hold the same key — so without the namespace two rows are
     * indistinguishable. Q-108 records the deviation.
     *
     * `key` and `tenantId` are left out: the first is the column above it and the second is the
     * tenant this whole console is acting for.
     */
    detail: [{ label: "Namespace", value: entry.namespace, mono: true }],
    history: [
      { label: "Created", value: asDay(entry.createdAt) },
      { label: "Last changed", value: asDay(entry.updatedAt) },
      { label: "Version", value: `v${String(entry.version)}` },
    ],
    glyph: "gavel",
  };
}

/** Which entries a chosen state leaves on the board. Client-side: the listing filters by namespace. */
export function filterEntries(
  entries: readonly ConfigurationEntry[],
  state: string,
): readonly ConfigurationEntry[] {
  if (state === "") return entries;
  return entries.filter((entry) => entryState(entry) === state);
}
